import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { IoIosArrowBack } from "react-icons/io";
import ChatView from "./ChatView";
import { IoTrashOutline } from "react-icons/io5";
import { LuSearch, LuX } from "react-icons/lu";
import { listProduct } from "../../../../services/productApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useDebounce from "../../../../hooks/useDebounce";
import { CircularProgress } from "@mui/material";
import { parseTopicFn } from "../../../../services/tinodeConsultationGroupService";
import SessionCountdownBanner from "./SessionCountdownBanner";
import { FaRegUser } from "react-icons/fa";
import { GlobalStateContext } from "../../../../components/context/GlobalStateContext";
import {
  addProductToRecipe,
  addSummaryConsultation,
  deleteProductRecipe,
  getDetailSummaryConsultation,
  getListConsultationHistory,
  getListProductRecipe,
  sendPrescription,
} from "../../../../services/telemedicineApi";
import { useNotification } from "../../../../components/context/NotificationContext";
import SendNotesDialog from "./SendNotesDialog";
import SendPrescriptionDialog from "./SendPrescriptionDialog";
import PrescriptionModal from "../../../../components/modalComp/PrescriptionModal";
import AttachmentModal from "../../../../components/modalComp/AttachmentModal";

const ChatDashboardDoctor = ({
  selectedTopic,
  topics,
  messages,
  messagesEndRef,
  messagesContainerRef,
  messageSeqSetRef,
  optimisticMessagesRef,
  sendOptimisticMessage,
  removeOptimisticMessage,
  tinodeRef,
  onBack,
  loadOlderMessages,
  isCurrentSessionOver,
  setOpenModalEndChat,
  isEndingSession,
  onSend,
  onSendPrescription,
  beforeSendMessage,
  sessionWindow,
}) => {
  const fileInputRef = useRef(null);
  const lastSaranRef = useRef(null);
  const searchInputRef = useRef(null);
  const queryClient = useQueryClient();

  const { isLogin } = useContext(GlobalStateContext);
  const { showNotification } = useNotification();

  const activeTopic = useMemo(
    () => topics.find((t) => (t.topic || t.id || t.name) === selectedTopic),
    [topics, selectedTopic],
  );

  const meta = useMemo(
    () => parseTopicFn(activeTopic?.public?.fn ?? activeTopic?.name ?? ""),
    [activeTopic],
  );

  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState(null);
  const [navigationTab, setNavigationTab] = useState(1);
  const [diagnosa, setDiagnosa] = useState("");
  const [saranList, setSaranList] = useState([""]);
  const [dataSearch, setDataSearch] = useState(null);
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState({});
  const [openModalNotes, setOpenModalNotes] = useState(false);
  const [openModalPrescription, setOpenModalPrescription] = useState(false);
  const [dataDetailPrescription, setDataDetailPrescription] = useState(null);
  const [openEResep, setOpenEResep] = useState(false);
  const [imgUrlSelect, setImgUrlSelect] = useState("");
  const [openModalPreviewImg, setOpenModalPreviewImg] = useState(false);

  const debounceSearch = useDebounce(search, 1000);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["detail-summary-consultation", activeTopic?.id],
    queryFn: () => getDetailSummaryConsultation(activeTopic?.id),
    enabled: !!isLogin && navigationTab === 1,
    keepPreviousData: false,
  });

  const { data: dataHistory, isFetching: historyFetching } = useQuery({
    queryKey: ["list-history-consultation", data?.consultation_id],
    queryFn: () => getListConsultationHistory(data?.consultation_id),
    enabled: !!(isLogin && data && navigationTab === 3),
    keepPreviousData: false,
  });

  const {
    data: dataProductRecipe,
    isFetching: recipeFetching,
    refetch: refetchRecipe,
  } = useQuery({
    queryKey: ["list-product-recipe", data?.consultation_id],
    queryFn: () => getListProductRecipe(data?.consultation_id),
    enabled: !!isLogin && !!data?.consultation_id && navigationTab === 2,
    keepPreviousData: false,
  });

  const { mutate: mutateList, isPending: isListLoading } = useMutation({
    mutationFn: listProduct,
    onSuccess: (data) => {
      if (data && data !== undefined) {
        setDataSearch(
          data.map((data) => ({
            ...data,
            isLoading: false,
          })),
        );
      }
    },
  });

  const { mutate: mutateAddSummary, isPending: isPendingSummary } = useMutation(
    {
      mutationFn: addSummaryConsultation,
      onSuccess: () => {
        showNotification("Berhasil mengirimkan catatan ke pasien", "success");
        refetch();
      },
    },
  );

  const {
    mutate: mutateAddProductRecipe,
    mutateAsync: mutateAddProductRecipeAsync,
    isPending,
  } = useMutation({
    mutationFn: addProductToRecipe,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["detail-summary-consultation"],
      });
    },
  });

  const { mutate: mutateDelete, isPending: isPendingDelete } = useMutation({
    mutationFn: deleteProductRecipe,
    onSuccess: () => {
      refetchRecipe();
      showNotification("Berhasil menghapus produk", "success");
    },
    onError: () => {
      refetchRecipe();
      showNotification("Gagal menghapus produk", "error");
    },
  });

  const { mutate: mutateSendPrescription, isPending: isPendingPrescription } =
    useMutation({
      mutationFn: sendPrescription,
      onSuccess: () => {
        showNotification("Berhasil mengirimkan resep ke pasien", "success");
        refetch();
      },
    });

  const allDosesFilled =
    dataProductRecipe &&
    dataProductRecipe.length > 0 &&
    dataProductRecipe.every((item) => {
      const localNote = notes[item.product_id] || "";
      const serverNote = item.notes || "";
      return localNote.trim() !== "" || serverNote.trim() !== "";
    });

  const hasEmptyNotes =
    !dataProductRecipe || dataProductRecipe.length === 0 || !allDosesFilled;

  const handleSendWithNotes = async (item) => {
    const note = notes[item.product_id] || "";
    if (!note.trim()) return null;

    const body = {
      consultation_id: data?.consultation_id,
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      price: item.price,
      indication: item.indication || "N/A",
      notes: note.trim(),
      company_id: data?.company_id,
    };

    try {
      await mutateAddProductRecipeAsync(body);
      return "ok";
    } catch {
      return "error";
    }
  };

  const handleSendAllDosesThenOpenModal = async () => {
    if (!dataProductRecipe || dataProductRecipe.length === 0) return;

    const itemsNeedingSave = dataProductRecipe.filter((item) => {
      const serverNote = item.notes || "";
      const localNote = notes[item.product_id] || "";
      return serverNote.trim() === "" && localNote.trim() !== "";
    });

    if (itemsNeedingSave.length > 0) {
      const results = await Promise.all(
        itemsNeedingSave.map((item) => handleSendWithNotes(item)),
      );
      console.log("Dosis tersimpan:", results);

      await refetchRecipe();
    }

    setOpenModalPrescription(true);
  };

  const handleAddProductToRecipe = (item) => {
    const qty = quantities[item.id] || 0;
    if (!qty || qty <= 0) return;

    const body = {
      consultation_id: data?.consultation_id,
      product_id: item.id,
      product_name: item.name,
      quantity: qty,
      price: item.price,
      indication: item.indication || "N/A",
      notes: "",
      company_id: data?.company_id,
    };

    mutateAddProductRecipe(body, {
      onSuccess: () => {
        setQuantities((prev) => ({ ...prev, [item.id]: 0 }));
        refetchRecipe();
      },
    });
  };

  const handleDeleteRecipe = (recipeId) => {
    mutateDelete(recipeId);
  };

  useEffect(() => {
    if (data) {
      const body = {
        limit: 3,
        offset: 1,
        keyword: "",
        company_id: data.company_id,
      };
      mutateList(body);
    }
  }, [data]);

  useEffect(() => {
    if (dataProductRecipe) {
      setNotes((prev) => {
        const updated = { ...prev };
        dataProductRecipe.forEach((item) => {
          if (
            item.notes &&
            item.notes.trim() !== "" &&
            !prev[item.product_id]
          ) {
            updated[item.product_id] = item.notes;
          }
        });
        return updated;
      });
    }
  }, [dataProductRecipe]);

  useEffect(() => {
    if (debounceSearch && debounceSearch !== "" && data) {
      const body = {
        limit: 5,
        offset: 1,
        keyword: debounceSearch,
        company_id: data?.company_id,
      };
      mutateList(body);
    }
  }, [debounceSearch]);

  useEffect(() => {
    if (lastSaranRef.current) {
      lastSaranRef.current.focus();
    }
  }, [saranList.length]);

  const handleClearSearch = () => {
    setSearch("");
    if (data) {
      mutateList({
        limit: 5,
        offset: 1,
        keyword: "",
        company_id: data.company_id,
      });
    }
    searchInputRef.current?.focus();
  };

  const handleSaranChange = (index, value) => {
    setSaranList((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleSaranKeyDown = (index, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSaranList((prev) => {
        const updated = [...prev];
        updated.splice(index + 1, 0, "");
        return updated;
      });
    }

    if (
      e.key === "Backspace" &&
      saranList[index] === "" &&
      saranList.length > 1
    ) {
      e.preventDefault();
      setSaranList((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleRemoveSaran = (index) => {
    if (saranList.length === 1) {
      setSaranList([""]);
      return;
    }
    setSaranList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddSaran = () => {
    setSaranList((prev) => [...prev, ""]);
  };

  const isValid =
    diagnosa.trim() !== "" && saranList.some((s) => s.trim() !== "");

  const handleSendNotes = () => {
    if (!isValid || isCurrentSessionOver) return;
    const filledSaran = saranList.filter((s) => s.trim() !== "");
    onSend({ diagnosa: diagnosa.trim(), saranList: filledSaran });
    mutateAddSummary({
      consultation_id: data && data.consultation_id,
      diagnose: diagnosa,
      suggestions: filledSaran,
    });
    setDiagnosa("");
    setSaranList([""]);
    setOpenModalNotes(false);
  };

  const handleSendPrescription = () => {
    if (
      !dataProductRecipe ||
      dataProductRecipe.length === 0 ||
      isCurrentSessionOver
    )
      return;

    onSendPrescription(dataProductRecipe);
    mutateSendPrescription(data?.consultation_id);
    setOpenModalPrescription(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 h-[64px] flex items-center justify-between bg-white border-b flex-shrink-0 relative">
        <div className="flex items-center gap-3">
          {onBack && (
            <div
              onClick={onBack}
              className="rounded-full p-1 cursor-pointer hover:bg-slate-200"
            >
              <IoIosArrowBack size={24} className="text-slate-500" />
            </div>
          )}
          <div className="relative">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              {meta.customerProfile === "" ? (
                <div className="bg-secondary w-full h-full flex items-center justify-center text-white">
                  <FaRegUser size={20} />
                </div>
              ) : (
                <img
                  src={meta.customerProfile}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>

            {activeTopic.online && (
              <div className="w-2 h-2 rounded-full bg-green-500 absolute bottom-0.5 right-0.5 border border-white" />
            )}
          </div>
          <div>
            <h1 className="font-semibold text-slate-700">
              {meta.customerName}
            </h1>
            <p className="text-slate-500 text-sm">
              {meta.customerType === 1 ? "Diri sendiri" : "Orang lain"} (
              {meta.customerAge}th /{" "}
              {meta.customerGender === 1 ? "Laki-laki" : "Perempuan"})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isCurrentSessionOver ? (
            <button
              onClick={() => setOpenModalEndChat(true)}
              disabled={isEndingSession}
              className="px-3 py-2 bg-error text-white text-md font-medium rounded-lg hover:bg-red-600 disabled:bg-gray-300 transition-colors"
            >
              {isEndingSession ? "Mengakhiri..." : "Akhiri Sesi"}
            </button>
          ) : (
            <span className="px-3 py-2 bg-slate-200 text-slate-500 text-md rounded-lg">
              Sesi Berakhir
            </span>
          )}
        </div>

        {sessionWindow && !isCurrentSessionOver ? (
          <div className="absolute -bottom-11 z-[999] left-4">
            <SessionCountdownBanner
              startAt={sessionWindow?.startAt}
              expiresAt={sessionWindow?.expiresAt}
              isSessionOver={isCurrentSessionOver}
              isCustomer={false}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 min-h-0">
        <ChatView
          selectedTopic={selectedTopic}
          tinodeRef={tinodeRef}
          messages={messages}
          messagesEndRef={messagesEndRef}
          messagesContainerRef={messagesContainerRef}
          messageSeqSetRef={messageSeqSetRef}
          optimisticMessagesRef={optimisticMessagesRef}
          sendOptimisticMessage={sendOptimisticMessage}
          removeOptimisticMessage={removeOptimisticMessage}
          loadOlderMessages={loadOlderMessages}
          setModalImageSrc={setModalImageSrc}
          setShowImageModal={setShowImageModal}
          isSessionOver={isCurrentSessionOver}
          fileInputRef={fileInputRef}
          fullWidth={false}
          beforeSend={beforeSendMessage}
          sessionWindow={sessionWindow}
        />

        <div className="w-2/5 bg-white border-l h-full flex flex-col overflow-hidden">
          <div className="px-4 pt-3 flex-shrink-0">
            <div className="border-b border-gray-200 w-full">
              <nav className="flex" aria-label="Tabs">
                <button
                  className={`w-full cursor-pointer whitespace-nowrap py-2 transform duration-300 border-b-2 font-medium rounded-t-lg text-base ${
                    navigationTab === 1
                      ? "border-primary text-primary hover:bg-indigo-100 bg-indigo-50"
                      : "border-transparent text-slate-500 hover:text-primary hover:border-primary hover:bg-indigo-100"
                  }`}
                  onClick={() => setNavigationTab(1)}
                >
                  <p className="font-semibold">Ringkasan</p>
                </button>
                <button
                  className={`w-full cursor-pointer whitespace-nowrap py-2 transform duration-300 border-b-2 font-medium rounded-t-lg text-base ${
                    navigationTab === 2
                      ? "border-primary text-primary hover:bg-indigo-100 bg-indigo-50"
                      : "border-transparent text-slate-500 hover:text-primary hover:border-primary hover:bg-indigo-100"
                  }`}
                  onClick={() => setNavigationTab(2)}
                >
                  <p className="font-semibold">Resep</p>
                </button>
                <button
                  className={`w-full cursor-pointer whitespace-nowrap py-2 transform duration-300 border-b-2 font-medium rounded-t-lg text-base ${
                    navigationTab === 3
                      ? "border-primary text-primary hover:bg-indigo-100 bg-indigo-50"
                      : "border-transparent text-slate-500 hover:text-primary hover:border-primary hover:bg-indigo-100"
                  }`}
                  onClick={() => setNavigationTab(3)}
                >
                  <p className="font-semibold">Riwayat</p>
                </button>
              </nav>
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col">
            {navigationTab === 1 && (
              <>
                {!isFetching ? (
                  <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-base">
                    <div>
                      <h1 className="text-primary font-semibold text-lg">
                        {data?.patient_name}
                      </h1>
                      <p className="text-slate-500 text-md">
                        {data?.patient_type}, {data?.age}th,{" "}
                        {data?.gender === 1 ? "Laki-laki" : "Perempuan"}
                      </p>
                    </div>

                    <div>
                      <h1 className="text-slate-700 font-medium">Subjective</h1>
                      <p className="text-slate-500 text-md">
                        {data?.patient_complaints}
                      </p>
                    </div>

                    {data?.product !== null && (
                      <div>
                        <h1 className="text-slate-700 font-medium mb-1">
                          Ingin konsultasi mengenai obat ini:
                        </h1>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 object-cover rounded-md overflow-hidden border border-gray-200 shrink-0">
                            <img
                              src={data?.product?.media}
                              alt="obat"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-slate-700">
                              {data?.product?.product_name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {data?.product?.indication}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {data && data.attachments?.length > 0 && (
                      <div>
                        <h1 className="text-slate-700 font-medium mb-1">
                          Gambar
                        </h1>
                        <div className="grid grid-cols-5 gap-2">
                          {data?.attachments?.map((img, index) => (
                            <div
                              key={index}
                              onClick={() => {
                                setImgUrlSelect(img);
                                setOpenModalPreviewImg(true);
                              }}
                              className="rounded-lg border border-slate-200 overflow-hidden aspect-square group cursor-pointer"
                            >
                              <img
                                src={img}
                                alt={`preview-${index}`}
                                className="w-full h-full object-cover transition duration-300 group-hover:scale-105"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {data?.diagnosis === "" || data?.suggestion === "" ? (
                      <div className="w-full">
                        <div className="mb-4">
                          <h1 className="text-slate-700 font-medium mb-1">
                            Diagnosa
                          </h1>
                          <input
                            type="text"
                            placeholder="Contoh: Demam, ISPA, Maag..."
                            value={diagnosa}
                            onChange={(e) => setDiagnosa(e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-1 focus:ring-secondary focus:border-transparent transition"
                          />
                        </div>

                        <div>
                          <h1 className="text-slate-700 font-medium mb-1">
                            Saran & Anjuran
                            <span className="text-sm text-slate-300 ml-1">
                              (Enter untuk baris baru)
                            </span>
                          </h1>

                          <div className="space-y-2">
                            {saranList.map((saran, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2"
                              >
                                <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>

                                <input
                                  ref={
                                    index === saranList.length - 1
                                      ? lastSaranRef
                                      : null
                                  }
                                  type="text"
                                  value={saran}
                                  placeholder={`Saran ${index + 1}...`}
                                  onChange={(e) =>
                                    handleSaranChange(index, e.target.value)
                                  }
                                  onKeyDown={(e) =>
                                    handleSaranKeyDown(index, e)
                                  }
                                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-1 focus:ring-secondary focus:border-transparent transition"
                                />

                                <button
                                  onClick={() => handleRemoveSaran(index)}
                                  className="text-error hover:text-red-700 transition"
                                >
                                  <IoTrashOutline size={20} />
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="w-full flex justify-end">
                            <button
                              onClick={handleAddSaran}
                              className="mt-2 text-md text-primary hover:text-indigo-700 font-medium"
                            >
                              + Tambah saran
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div>
                          <h1 className="text-slate-700 font-medium">
                            Diagnosa
                          </h1>
                          <p className="text-slate-500 text-md">
                            {data?.diagnosis}
                          </p>
                        </div>

                        <div>
                          <h1 className="text-slate-700 font-medium">Saran</h1>

                          {data?.suggestion?.length > 0 ? (
                            <ul className="list-disc pl-5 text-slate-500 text-md space-y-0">
                              {data.suggestion.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-slate-400 text-md">
                              Tidak ada saran
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-3 animate-pulse space-y-4">
                    {[1, 2, 3, 4].map((item) => (
                      <div key={item}>
                        <div className="h-6 bg-slate-200 rounded-lg w-2/3 mb-2" />
                        <div className="h-4 bg-slate-200 rounded w-1/3" />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {navigationTab === 1 && !isCurrentSessionOver ? (
              <div className="flex-shrink-0 px-4 py-3 bg-white">
                <button
                  type="button"
                  disabled={!isValid || isCurrentSessionOver}
                  onClick={() => setOpenModalNotes(true)}
                  className="h-[40px] border text-white flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none transform duration-200 hover:bg-secondaryHover border-secondary bg-secondary disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-default"
                >
                  Kirim ke Chat
                </button>
              </div>
            ) : null}
            {navigationTab === 2 && (
              <>
                {data && data.is_sended && !recipeFetching ? (
                  <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-4 text-base">
                    {dataProductRecipe && !recipeFetching
                      ? dataProductRecipe.map((item, index) => (
                          <div key={index} className="text-slate-700">
                            <h1 className="font-semibold">
                              {item.product_name}
                            </h1>
                            <p className="text-slate-500 text-md">
                              Qty: {item.quantity} {item.uom}
                            </p>
                            {item.indication !== "N/A" && (
                              <p className="text-slate-500 text-md">
                                {item.indication}
                              </p>
                            )}
                            <p className="text-slate-500 font-medium text-md">
                              Dosis: {item.notes === "" ? "-" : item.notes}
                            </p>
                          </div>
                        ))
                      : null}
                  </div>
                ) : recipeFetching ? (
                  <div className="px-4 py-3 animate-pulse space-y-4">
                    {[1, 2, 3, 4].map((item) => (
                      <div key={item}>
                        <div className="h-6 bg-slate-200 rounded-lg w-2/3 mb-2" />
                        <div className="h-4 bg-slate-200 rounded w-1/3" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-4 text-base">
                    <div className="relative flex items-center top-0 bg-white pt-1 pb-2 z-10">
                      <LuSearch
                        size={18}
                        className="absolute left-3 text-gray-400 pointer-events-none"
                      />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={search}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSearch(val);
                          if (val === "" && data) {
                            mutateList({
                              limit: 3,
                              offset: 1,
                              keyword: "",
                              company_id: data.company_id,
                            });
                          }
                        }}
                        placeholder="Cari nama obat..."
                        className="w-full pl-9 pr-8 py-2.5 text-md border border-gray-200 rounded-full bg-gray-50 focus:bg-white focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition-all"
                      />
                      {search && (
                        <button
                          onClick={handleClearSearch}
                          className="absolute right-3 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <LuX size={16} />
                        </button>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <p className="text-md text-slate-500 font-medium uppercase tracking-wide px-1">
                        {search.trim() !== ""
                          ? "Hasil Pencarian"
                          : "Suggest Produk"}
                      </p>

                      {isListLoading ? (
                        [1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className="border border-gray-200 rounded-xl p-3 bg-white animate-pulse"
                          >
                            <div className="flex items-start justify-between gap-2 mb-3">
                              <div className="flex-1 space-y-2">
                                <div className="h-4 bg-slate-200 rounded w-3/4" />
                                <div className="h-3 bg-slate-200 rounded w-1/3" />
                              </div>
                              <div className="w-8 h-8 bg-slate-200 rounded-lg" />
                            </div>
                            <div className="h-9 bg-slate-200 rounded-lg w-full" />
                          </div>
                        ))
                      ) : dataSearch && dataSearch.length > 0 ? (
                        dataSearch.map((item) => {
                          const qty = quantities[item.id] || 0;
                          return (
                            <div
                              key={item.id}
                              className="border border-gray-200 rounded-xl px-3 py-2 bg-white flex justify-between w-full"
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                  <h1 className="font-medium text-slate-700 leading-tight line-clamp-2">
                                    {item.name}
                                  </h1>
                                  <p className="text-sm text-slate-400 mt-0.5">
                                    {item.indication || ""}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-1 flex-shrink-0">
                                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                                  <button
                                    onClick={() =>
                                      setQuantities((prev) => ({
                                        ...prev,
                                        [item.id]: Math.max(
                                          0,
                                          (prev[item.id] || 0) - 1,
                                        ),
                                      }))
                                    }
                                    disabled={qty <= 0}
                                    className="w-6 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 disabled:text-slate-300 transition font-bold text-base"
                                  >
                                    −
                                  </button>
                                  <input
                                    type="number"
                                    min={0}
                                    value={qty === 0 ? "" : qty}
                                    placeholder="0"
                                    onChange={(e) => {
                                      const val = parseInt(e.target.value) || 0;
                                      setQuantities((prev) => ({
                                        ...prev,
                                        [item.id]: Math.max(0, val),
                                      }));
                                    }}
                                    className="w-6 text-center text-md py-1 outline-none bg-transparent text-slate-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                  <button
                                    onClick={() =>
                                      setQuantities((prev) => ({
                                        ...prev,
                                        [item.id]: (prev[item.id] || 0) + 1,
                                      }))
                                    }
                                    className="w-6 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 transition font-bold text-base"
                                  >
                                    +
                                  </button>
                                </div>

                                <button
                                  onClick={() => handleAddProductToRecipe(item)}
                                  disabled={qty <= 0 || isPending}
                                  className="h-8 w-8 rounded-lg border border-secondary bg-secondary text-white font-bold hover:bg-secondaryHover disabled:bg-slate-200 disabled:border-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-lg leading-none"
                                >
                                  {isPending ? (
                                    <CircularProgress
                                      size={12}
                                      className="text-white"
                                    />
                                  ) : (
                                    "+"
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-6 text-slate-400 text-sm">
                          Produk tidak ditemukan
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <p className="text-md text-slate-500 font-medium uppercase tracking-wide px-1">
                        Daftar Obat Sementara
                      </p>
                      {recipeFetching ? (
                        [1, 2].map((i) => (
                          <div
                            key={i}
                            className="border border-gray-200 rounded-xl p-3 bg-white animate-pulse"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="space-y-2 flex-1">
                                <div className="h-4 bg-slate-200 rounded w-2/3" />
                                <div className="h-3 bg-slate-200 rounded w-1/4" />
                              </div>
                              <div className="w-7 h-7 bg-slate-200 rounded-lg ml-2" />
                            </div>
                            <div className="h-9 bg-slate-200 rounded-lg w-full" />
                          </div>
                        ))
                      ) : dataProductRecipe && dataProductRecipe.length > 0 ? (
                        dataProductRecipe.map((item) => {
                          return (
                            <div
                              key={item.id}
                              className="border border-gray-200 rounded-xl p-3 bg-white"
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                  <h1 className="font-medium text-slate-700 leading-tight">
                                    {item.product_name}
                                  </h1>
                                  <p className="text-sm text-slate-500 mt-1">
                                    {item.indication &&
                                      item.indication !== "N/A" && (
                                        <span>{item.indication}</span>
                                      )}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    Qty: {item.quantity} {item.uom}
                                  </p>
                                </div>
                                <button
                                  onClick={() =>
                                    handleDeleteRecipe(item.recipe_id)
                                  }
                                  className="p-1.5 text-slate-400 hover:text-error hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                  title="Hapus dari resep"
                                >
                                  <IoTrashOutline size={17} />
                                </button>
                              </div>

                              {item.notes &&
                              item.notes.trim() !== "" &&
                              !(
                                notes[item.product_id] !== undefined &&
                                notes[item.product_id] !== item.notes
                              ) ? (
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-md text-slate-500 flex-1">
                                    <span className="font-medium text-slate-700">
                                      Dosis:
                                    </span>{" "}
                                    {item.notes}
                                  </p>
                                </div>
                              ) : (
                                <div
                                  className={`flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-gray-50 focus-within:ring-1 transition ${
                                    notes[item.product_id]?.trim()
                                      ? "border-secondary focus-within:border-secondary focus-within:ring-secondary"
                                      : "border-orange-300 focus-within:border-orange-400 focus-within:ring-orange-300"
                                  }`}
                                >
                                  <input
                                    type="text"
                                    value={notes[item.product_id] || ""}
                                    onChange={(e) =>
                                      setNotes((prev) => ({
                                        ...prev,
                                        [item.product_id]: e.target.value,
                                      }))
                                    }
                                    placeholder="Masukkan dosis konsumsi..."
                                    className="flex-1 text-md bg-transparent outline-none text-slate-700 placeholder-slate-400"
                                  />
                                  <span className="flex-shrink-0">
                                    {notes[item.product_id]?.trim() ? (
                                      <span className="text-secondary text-xs font-bold">
                                        ✓
                                      </span>
                                    ) : (
                                      <span className="text-orange-400 text-xs">
                                        !
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">
                          Belum ada obat yang ditambahkan
                        </div>
                      )}
                      {hasEmptyNotes && (
                        <p className="text-sm italic text-slate-300">
                          Silahkan tambahkan dosis pada obat sebelum mengirim
                          resep ke pasien.
                        </p>
                      )}
                    </div>
                    <div className="h-2" />
                  </div>
                )}
              </>
            )}
            {navigationTab === 2 &&
            !isCurrentSessionOver &&
            !data?.is_sended ? (
              <div className="flex-shrink-0 px-4 py-3 bg-white border-t">
                {hasEmptyNotes &&
                  dataProductRecipe &&
                  dataProductRecipe.length > 0 && (
                    <p className="text-xs italic text-slate-400 text-center mb-2">
                      Lengkapi dosis semua obat sebelum mengirim resep.
                    </p>
                  )}
                <button
                  type="button"
                  disabled={
                    hasEmptyNotes ||
                    isCurrentSessionOver ||
                    isPending ||
                    isPendingPrescription
                  }
                  onClick={handleSendAllDosesThenOpenModal}
                  className="h-[40px] border text-white flex items-center gap-2 justify-center w-full tracking-wide font-medium rounded-lg focus:outline-none transform duration-200 hover:bg-secondaryHover border-secondary bg-secondary disabled:border-slate-300 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  {isPending ? (
                    <>
                      <CircularProgress size={14} sx={{ color: "white" }} />
                      <span>Menyimpan dosis...</span>
                    </>
                  ) : (
                    "Kirim ke Chat"
                  )}
                </button>
              </div>
            ) : null}
            {navigationTab === 3 && (
              <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2 text-base">
                <div className="flex flex-col gap-2">
                  {dataHistory && !historyFetching ? (
                    dataHistory.map((item, index, arr) => (
                      <div key={item}>
                        <div className="flex items-center justify-between text-slate-700">
                          <div className="">
                            <h1 className="font-semibold text-base">
                              {item.consultation_date}
                            </h1>
                            <p className="font-medium text-md">
                              {item.diagnosis || "-"}
                            </p>
                            <p className="text-slate-500 text-md">
                              {item.doctor_name}
                            </p>
                          </div>
                          {item.recipes?.length > 0 && (
                            <button
                              onClick={() => {
                                setDataDetailPrescription(item);
                                setOpenEResep(true);
                              }}
                              className="py-1.5 px-2 text-white w-fit tracking-wide text-md font-medium rounded-lg focus:outline-none transform duration-200 hover:bg-secondaryHover bg-secondary disabled:bg-slate-300 disabled:cursor-default"
                            >
                              e-Resep
                            </button>
                          )}
                        </div>
                        {index !== arr.length - 1 && <hr className="my-2" />}
                      </div>
                    ))
                  ) : historyFetching ? (
                    <div className="animate-pulse space-y-4">
                      {[1, 2, 3, 4].map((item) => (
                        <div key={item}>
                          <div className="h-6 bg-slate-200 rounded-lg w-2/3 mb-2" />
                          <div className="h-4 bg-slate-200 rounded w-1/3" />
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showImageModal && modalImageSrc && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[99999] p-5"
          onClick={() => setShowImageModal(false)}
        >
          <img
            src={modalImageSrc}
            alt="Preview"
            className="max-w-[90%] max-h-[90%] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-5 right-5 bg-white/20 text-white border-none rounded-full w-10 h-10 text-2xl cursor-pointer flex items-center justify-center"
          >
            ×
          </button>
        </div>
      )}

      <SendNotesDialog
        open={openModalNotes}
        onClose={() => setOpenModalNotes(false)}
        handleSend={handleSendNotes}
        diagnosis={diagnosa}
        suggest={saranList}
      />

      <SendPrescriptionDialog
        open={openModalPrescription}
        onClose={() => setOpenModalPrescription(false)}
        handleSend={handleSendPrescription}
        dataProduct={dataProductRecipe}
      />

      <PrescriptionModal
        open={openEResep}
        setOpen={setOpenEResep}
        data={dataDetailPrescription}
      />

      <AttachmentModal
        open={openModalPreviewImg}
        setOpen={setOpenModalPreviewImg}
        imgUrl={imgUrlSelect}
      />
    </div>
  );
};

export default ChatDashboardDoctor;
